import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { Button } from '@/components/ui/Button';
import {
  useCancelPaymentRequest,
  useCancelProviderPaymentRequest,
} from '@/hooks/useInvoiceMobilePayRequest';
import { providerPayPageUrl } from '@/lib/mobilePayRequest';
import { UI_MUTED } from '@/lib/typography';

export function ProviderPaymentRequest({
  invoiceId,
  payToken,
  requestId,
  title,
  hideHeading = false,
  onCancelled,
}: {
  invoiceId: string;
  payToken: string | null;
  requestId?: string | null;
  title?: string;
  hideHeading?: boolean;
  onCancelled?: () => void;
}) {
  const { t } = useTranslation();
  const cancelOne = useCancelPaymentRequest();
  const cancelInvoiceRequests = useCancelProviderPaymentRequest();
  const cancelPending = requestId ? cancelOne.isPending : cancelInvoiceRequests.isPending;
  const [confirming, setConfirming] = useState(false);
  const [copied, setCopied] = useState(false);
  const payUrl = payToken ? providerPayPageUrl(window.location.origin, payToken) : null;

  async function confirmCancel() {
    try {
      if (requestId) {
        await cancelOne.mutateAsync({ requestId, invoiceId });
      } else {
        await cancelInvoiceRequests.mutateAsync(invoiceId);
      }
      setConfirming(false);
      toast.success(t('invoices.mobilepay_request_cancelled'));
      onCancelled?.();
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : t('invoices.mobilepay_request_cancel_failed'),
      );
    }
  }

  return (
    <div className="space-y-3" data-payment-request="pending">
      {hideHeading ? null : (
        <div>
          <h6 className="text-sm font-medium text-ds-text">
            {title ?? t('invoices.mobilepay_request_pending_title')}
          </h6>
          <p className={`mt-1 ${UI_MUTED}`}>{t('invoices.mobilepay_request_note')}</p>
        </div>
      )}
      {payUrl ? (
        <div className="flex items-center gap-2">
          <a
            href={payUrl}
            target="_blank"
            rel="noreferrer"
            className="min-w-0 flex-1 truncate rounded-md border border-ds-field bg-ds-sunk px-3 py-2 text-xs text-ds-teal underline"
            data-payment-request-link=""
          >
            {payUrl}
          </a>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => {
              void navigator.clipboard.writeText(payUrl).then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
              });
            }}
          >
            {copied
              ? t('appointments.payment_choice.copied')
              : t('appointments.payment_choice.copy_link')}
          </Button>
        </div>
      ) : (
        <p className={UI_MUTED}>{t('invoices.mobilepay_request_link_missing')}</p>
      )}
      {confirming ? (
        <div className="space-y-2 rounded-md border border-ds-line p-3">
          <p className="text-sm font-medium text-ds-text">
            {t('invoices.mobilepay_request_cancel_title')}
          </p>
          <p className={UI_MUTED}>{t('invoices.mobilepay_request_cancel_body')}</p>
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setConfirming(false)}
              disabled={cancelPending}
            >
              {t('invoices.mobilepay_request_cancel_back')}
            </Button>
            <Button
              type="button"
              onClick={() => {
                void confirmCancel();
              }}
              disabled={cancelPending}
              data-cancel-payment-request=""
            >
              {t('invoices.mobilepay_request_cancel')}
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex justify-end">
          <Button type="button" variant="secondary" onClick={() => setConfirming(true)}>
            {t('invoices.mobilepay_request_cancel')}
          </Button>
        </div>
      )}
    </div>
  );
}
